import { AfterViewInit, ChangeDetectorRef, Component, ContentChildren, ElementRef, forwardRef, Injector, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, AfterContentInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { Dropdown, DropdownChangeEvent } from 'primeng/dropdown';
import { Nullable } from 'primeng/ts-helpers';
import { NxTemplateDirective } from '../../directives/nx-template.directive';

export interface EditSelectable {
    name: string;
    id: string;
    disabled?: boolean;
}

@Component({
    selector: 'nx-edit-select-v2-input',
    template: `
    <div class="nx-edit-select">
        <label *ngIf="showLabel" for="select">{{label | translate}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <p-dropdown
            #dropdown
            [optionLabel]="optionLabel"
            [dataKey]="dataKey"
            [optionValue]="optionValue"
            [options]="options"
            [filter]="filter"
            [(ngModel)]="selected"
            (onChange)="onInputChange($any($event))"
            [appendTo]="'body'"
            [virtualScroll]="false"
            [disabled]="isDisabled"
            [panelStyleClass]="'nx-edit-select' + (panelWidhtFull ? ' full-width' : '')"
            [autoDisplayFirst]=false
            [showClear]="showClear"
            [placeholder]="placeholder | translate"
            [emptyMessage]="emptyMessage | translate"
            [optionDisabled]="optionDisabled"
            [optionGroupLabel]="optionGroupLabel"
            [optionGroupChildren]="optionGroupChildren"
            [group]="!!optionGroupLabel"
            [resetFilterOnHide]="true">
            <ng-template *ngIf="onFilter" pTemplate="filter" let-options="options">
                <input
                    type="text"
                    pInputText
                    #filterElement
                    class="p-dropdown-filter p-inputtext p-component"
                    autocomplete="off"
                    role="textbox"
                    aria-autocomplete="list"
                    aria-controls="dropdown-list"
                    aria-activedescendant="focusedOptionId"
                    (input)="onFilterInputChange($event)"
                />
            </ng-template>
            <ng-template let-value pTemplate="selectedItem" *ngIf="selectedItemTemplate && selected">
                <ng-container *ngTemplateOutlet="selectedItemTemplate; context: { $implicit: value }"></ng-container>
            </ng-template>
            <ng-template let-item pTemplate="item" *ngIf="itemTemplate">
                <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item }"></ng-container>
            </ng-template>
            <ng-template let-group pTemplate="group" *ngIf="groupTemplate">
                <ng-container *ngTemplateOutlet="groupTemplate; context: { $implicit: group }"></ng-container>
            </ng-template>
        </p-dropdown>
        <small *ngIf="helpText">{{helpText | translate}}</small>
    </div>
  `,
    styles: [`
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditSelectV2InputComponent),
            multi: true
        }
    ]
})
export class EditSelectV2InputComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy, AfterContentInit {

    /**
     * Reference to the underlying PrimeNG Dropdown component
     */
    @ViewChild('dropdown') pDropdown: Dropdown;

    /**
     * Reference to the filter input element (if filter is enabled)
     */
    @ViewChild('filterElement') filterElement: ElementRef<HTMLInputElement>;

    // native options
    /**
     * A property to define the label of an option in the options collection:
     * What will be shown to the user.
     * IMPORTANT: When using groups, this must be set to a property that exists on the items
     * (not the groups), even if you use a custom item template.
     */
    @Input() optionLabel: string | undefined;

    /**
     * Property name that holds the label of a group (optional, for grouped options).
     * When set, enables grouped dropdown display.
     */
    @Input() optionGroupLabel: string | undefined;

    /**
     * Property name that holds the items of a group (optional, for grouped options).
     * Should contain the array of child items for each group.
     */
    @Input() optionGroupChildren: string | undefined;

    /**
     * A property to uniquely identify an option in the options collection:
     * Used to track selections.
     */
    @Input() dataKey: string | undefined;

    /**
     * A property to define the value of an option in the options collection:
     * What will be returned on selection.
     */
    @Input() optionValue: string | undefined;

    /**
     * Placeholder text to show when no item is selected.
     */
    @Input() placeholder: string = 'coreModule.editSelectV2InputComponent.placeholder';

    /**
     * Message to show when no options are available (after filtering).
     */
    @Input() emptyMessage: string = 'coreModule.editSelectV2InputComponent.emptyMessage';

    /**
     * The options that should be shown in the dropdown
     */
    @Input() options: any[];

    /**
     * If true, a filter input will be shown to allow filtering the options.
     */
    @Input() filter = false;

    /**
     * A property to define if an option is disabled
     */
    @Input() optionDisabled: string | undefined;

    // nx modul options
    /**
     * If true, the panel will have full width of the parent.
     */
    @Input() panelWidhtFull = false;

    /**
     * Help text that will be shown below the input field.
     */
    @Input() helpText: string;

    /**
     * Label that will be shown above the input field.
     */
    @Input() label: string;

    /**
     * If true, the label will be shown above the input field.
     */
    @Input() showLabel = true;

    /**
     * If true, a clear icon will be shown to allow clearing the selection.
     */
    @Input() showClear = false;

    private mutationObserver: MutationObserver;

    ngControl: NgControl;
    isDisabled: boolean;

    @Input() onFilter: (value: string) => void;
    @Input() filterHint: string;

    selected: any;

    public selectedItemTemplate: TemplateRef<any> | undefined;
    public itemTemplate: TemplateRef<any> | undefined;
    public groupTemplate: TemplateRef<any> | undefined;

    private onChange: (value: string) => void;
    private onTouched: () => void;

    @ContentChildren(NxTemplateDirective) templates!: QueryList<NxTemplateDirective>;

    constructor(
        private _injector: Injector,
        private cdr: ChangeDetectorRef
    ) {
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
        this.observeClassChanges();
    }

    ngAfterContentInit() {
        this.templates.forEach((item) => {
            switch (item.getType()) {
                case 'selectedItem':
                    this.selectedItemTemplate = item.template;
                    break;
                case 'item':
                    this.itemTemplate = item.template;
                    break;
                case 'group':
                    this.groupTemplate = item.template;
                    break;
            }
        });
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    ngOnDestroy() {
        this.mutationObserver?.disconnect();
      }

    writeValue(selected: any): void {
        this.selected = selected;
    }

    registerOnChange(fn: (selected: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    onInputChange(event: DropdownChangeEvent): void {
        if (this.onChange) {
            this.onChange(this.selected);
        }
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    /**
     * Workaround: for whatever reason the dropdown element does not fire the blur event. We work
     * around this by observing the class changes: when the class p-inputwrapper-focus gets removed,
     * we fire the event and inform the form control that the input has been touched.
     */
    observeClassChanges() {
        const targetNode = this.pDropdown?.el?.nativeElement;
        if (!targetNode) {
          // target not found
          return;
        }
        this.mutationObserver = new MutationObserver((mutationsList) => {
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const classList = (mutation.target as HTMLElement).classList;
              if (!classList.contains('p-inputwrapper-focus')) {
                this.onBlur();
              }
            }
          }
        });
        this.mutationObserver.observe(targetNode, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

    onFilterInputChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const filterValue = inputElement.value;
        if (this.onFilter) {
            this.onFilter(filterValue);
        }
    }

    /**
     * Opens the dropdown and focuses the filter input
     */
    openAndFocusFilter(): void {
        // Open the dropdown
        this.pDropdown.show();

        // Wait for the dropdown panel to render, then focus the filter input
        setTimeout(() => {
            if (this.filterElement?.nativeElement) {
                this.filterElement.nativeElement.focus();
            }
        }, 10);
    }

}
